import { Box, Avatar, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import {
  CustomTableComponent,
  Column,
} from "../../../shared/elements/customTable.component";
import { PagerComponent } from "../../../shared/elements/pager.component";
import { CustomButtonComponent } from "../../../shared/elements/cutomBtn.component";
import { CustomDialogComponent } from "../../../shared/elements/customDialog.component";
import { CustomInputComponent } from "../../../shared/elements/customInputs.component";
import { AdminDto } from "../dto/index.dto";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import {
  useCreateAdminHook,
  useDeleteAdminHook,
  useGetAdminsHook,
  useUpdateAdminHook,
} from "../hooks/settings.hook";
import { NotificationsComponent } from "../../notifications/components/notifications.component";
import { RevenueComponent } from "../../revenue/components/revenue.component";

type TabType = "Admins" | "Revenus" | "Notification";
type DialogMode = "create" | "edit";

const columns: Column[] = [
  { key: "fullName", label: "Nom complet" },
  { key: "email", label: "Adresse email" },
  { key: "role", label: "Rôle" },
];

const emptyFormData = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
};

export const SettingsComponent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("Admins");
  const [selectedAdmin, setSelectedAdmin] = useState<AdminDto | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>("create");
  const [formData, setFormData] = useState(emptyFormData);
  const [formError, setFormError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const { data: admins, isLoading, error } = useGetAdminsHook(page);
  const createAdminMutation = useCreateAdminHook();
  const updateAdminMutation = useUpdateAdminHook();
  const deleteAdminMutation = useDeleteAdminHook();

  const rows = admins?.data || [];
  const currentSelectedAdmin =
    rows.find((admin) => admin.id === selectedAdmin?.id) || rows[0] || null;

  const handleRowClick = (row: AdminDto) => {
    setSelectedAdmin(row);
  };

  const handleAddAdmin = () => {
    setDialogMode("create");
    setFormData(emptyFormData);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleModify = () => {
    if (!currentSelectedAdmin) return;
    setDialogMode("edit");
    setFormData({
      firstName: currentSelectedAdmin.firstName,
      lastName: currentSelectedAdmin.lastName,
      email: currentSelectedAdmin.email,
      password: "",
    });
    setFormError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData(emptyFormData);
    setFormError(null);
  };

  const handleDelete = () => {
    if (!currentSelectedAdmin) return;
    deleteAdminMutation.mutate(currentSelectedAdmin.id, {
      onSuccess: () => setSelectedAdmin(null),
    });
  };

  const handleConfirm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setFormError("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    if (dialogMode === "create" && formData.password.length < 8) {
      setFormError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (
      dialogMode === "edit" &&
      formData.password &&
      formData.password.length < 8
    ) {
      setFormError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    if (dialogMode === "create") {
      createAdminMutation.mutate(
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
        },
        {
          onSuccess: () => handleCloseDialog(),
          onError: (err: any) =>
            setFormError(
              err?.response?.data?.message || "Une erreur est survenue."
            ),
        }
      );
    } else if (currentSelectedAdmin) {
      updateAdminMutation.mutate(
        {
          id: currentSelectedAdmin.id,
          data: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            ...(formData.password ? { password: formData.password } : {}),
          },
        },
        {
          onSuccess: (updated) => {
            setSelectedAdmin(updated);
            handleCloseDialog();
          },
          onError: (err: any) =>
            setFormError(
              err?.response?.data?.message || "Une erreur est survenue."
            ),
        }
      );
    }
  };

  const handleInputChange =
    (field: keyof typeof formData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: event.target.value });
    };

  const isSaving = createAdminMutation.isPending || updateAdminMutation.isPending;

  return (
    <Box p={2}>
      {/* Tabs */}
      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        {(["Admins", "Revenus", "Notification"] as TabType[]).map((tab) => (
          <Box
            key={tab}
            onClick={() => setActiveTab(tab)}
            sx={{
              background:
                activeTab === tab
                  ? "linear-gradient(to bottom, #A0005C, #FF0080)"
                  : "transparent",
              color: activeTab === tab ? "#fff" : "#B4006E",
              border: activeTab === tab ? "none" : "2px solid #B4006E",
              borderRadius: "20px",
              padding: "8px 28px",
              fontWeight: "bold",
              cursor: "pointer",
              fontSize: "13px",
              textAlign: "center",
              minWidth: "110px",
              transition: "all 0.3s ease",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            {tab}
          </Box>
        ))}
        <Box flexGrow={1} sx={{ display: { xs: "none", sm: "block" } }} />
        {activeTab === "Admins" && (
          <CustomButtonComponent
            label="Ajouter un admin"
            onClick={handleAddAdmin}
            sx={{
              width: "160px",
              height: "34px",
              padding: "6px 14px",
              fontSize: "13px",
              backgroundColor: "transparent",
              border: "1px solid #B4006E",
              color: "#B4006E",
            }}
          />
        )}
      </Box>

      {/* Content Area */}
      <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2}>
        {/* Left side - Table */}
        <Box flex={1} sx={{ minWidth: 0 }}>
          {activeTab === "Admins" && (
            <>
              {isLoading && (
                <Box p={2} textAlign="center" color="#999" fontSize="0.85rem">
                  Chargement...
                </Box>
              )}
              {!isLoading && error && (
                <Box p={2} textAlign="center" color="#c0392b" fontSize="0.85rem">
                  Erreur lors du chargement des admins.
                </Box>
              )}
              {!isLoading && !error && (
                <>
                  <CustomTableComponent
                    columns={columns}
                    rows={rows}
                    handleClick={handleRowClick}
                  />
                  {admins && (
                    <PagerComponent
                      page={admins.page}
                      totalPages={admins.totalPages}
                      total={admins.total}
                      onPageChange={setPage}
                    />
                  )}
                </>
              )}
            </>
          )}
          {activeTab === "Revenus" && <RevenueComponent />}
          {activeTab === "Notification" && <NotificationsComponent />}
        </Box>

        {/* Right side - Admin Details */}
        {activeTab === "Admins" && currentSelectedAdmin && (
          <Box
            sx={{
              width: { xs: "100%", md: "260px" },
              flexShrink: 0,
              backgroundColor: "#FFFDF7",
              borderRadius: "16px",
              padding: "22px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            {/* Avatar with camera icon */}
            <Box position="relative">
              <Avatar
                src={currentSelectedAdmin.avatar || ""}
                alt={currentSelectedAdmin.fullName}
                sx={{
                  width: 88,
                  height: 88,
                  border: "3px solid #B4006E",
                }}
              />
              <IconButton
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  backgroundColor: "#B4006E",
                  color: "white",
                  width: 28,
                  height: 28,
                  "&:hover": {
                    backgroundColor: "#900050",
                  },
                }}
              >
                <CameraAltIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {/* Admin Details */}
            <Box width="100%" display="flex" flexDirection="column" gap={1.5}>
              <Box>
                <Box fontSize="12px" fontWeight="bold" color="#333" mb={0.5}>
                  Nom complet :
                </Box>
                <Box fontSize="13px" color="#555">
                  {currentSelectedAdmin.fullName}
                </Box>
              </Box>

              <Box>
                <Box fontSize="12px" fontWeight="bold" color="#333" mb={0.5}>
                  Adresse email :
                </Box>
                <Box fontSize="13px" color="#555">
                  {currentSelectedAdmin.email}
                </Box>
              </Box>

              <Box>
                <Box fontSize="12px" fontWeight="bold" color="#333" mb={0.5}>
                  Rôle :
                </Box>
                <Box fontSize="13px" color="#555">
                  {currentSelectedAdmin.role}
                </Box>
              </Box>
            </Box>

            {/* Actions */}
            <Box
              width="100%"
              display="flex"
              justifyContent="space-between"
              marginTop="auto"
            >
              <Typography
                onClick={handleModify}
                sx={{
                  color: "#B4006E",
                  fontWeight: "bold",
                  fontSize: "13px",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Modifier
              </Typography>
              <Typography
                onClick={handleDelete}
                sx={{
                  color: "#c0392b",
                  fontWeight: "bold",
                  fontSize: "13px",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Supprimer
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Add / Edit Admin Dialog */}
      <CustomDialogComponent
        open={openDialog}
        onClose={handleCloseDialog}
        title={dialogMode === "create" ? "Ajouter un admin" : "Modifier l'admin"}
        onConfirm={handleConfirm}
        confirmLabel={dialogMode === "create" ? "Ajouter" : "Enregistrer"}
        cancelLabel="Annuler"
      >
        <Box display="flex" flexDirection="column" gap={3} py={2}>
          <CustomInputComponent
            label="Prénom"
            value={formData.firstName}
            onChange={handleInputChange("firstName")}
            placeholder="Entrez le prénom"
          />
          <CustomInputComponent
            label="Nom"
            value={formData.lastName}
            onChange={handleInputChange("lastName")}
            placeholder="Entrez le nom"
          />
          <CustomInputComponent
            label="Adresse email"
            value={formData.email}
            onChange={handleInputChange("email")}
            type="email"
            placeholder="Entrez l'adresse email"
          />
          <CustomInputComponent
            label={
              dialogMode === "create"
                ? "Mot de passe"
                : "Nouveau mot de passe (optionnel)"
            }
            value={formData.password}
            onChange={handleInputChange("password")}
            type="password"
            placeholder="Entrez le mot de passe"
          />
          {formError && (
            <Box color="#c0392b" fontSize="0.8rem">
              {formError}
            </Box>
          )}
          {isSaving && (
            <Box color="#999" fontSize="0.8rem">
              Enregistrement...
            </Box>
          )}
        </Box>
      </CustomDialogComponent>
    </Box>
  );
};
